import React, { useEffect, useRef, useState } from "react";
import moment from "moment";
import URL from "../../../../Main/url";
import CurrencyWord from "../../../../Componets/Currencywords";
import formatCountryName from "../../../../Main/formatCountryName ";
import UserCurrency from "../../../../Componets/UserCurrency";

const InvoiceA5 = React.forwardRef((props, ref) => {
  const { values } = props;

  const headerRef = useRef(null);
  const tableHeaderRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [tableHeaderHeight, setTableHeaderHeight] = useState(0);

  const itemsPerPage = 5;
  const [totalPages, setTotalPages] = useState(1);

  const capitalizeWords = (str) => {
    if (!str) return "";
    return str.replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const calculateTotalPages = () => {
    if (!values?.details) return 1;
    return Math.ceil(values.details.length / itemsPerPage);
  };

  const getItemsForPage = (page) => {
    if (!values?.details) return [];
    const startIndex = (page - 1) * itemsPerPage;
    return values.details.slice(startIndex, startIndex + itemsPerPage);
  };

  useEffect(() => {
    if (values?.details) {
      setTotalPages(calculateTotalPages());
    }
  }, [values?.details]);

  useEffect(() => {
    if (headerRef.current) setHeaderHeight(headerRef.current.offsetHeight);
    if (tableHeaderRef.current)
      setTableHeaderHeight(tableHeaderRef.current.offsetHeight);
  }, [values]);

  useEffect(() => {
    if (headerHeight && tableHeaderHeight) {
      injectScopedStyles(headerHeight, tableHeaderHeight);
    }
  }, [headerHeight, tableHeaderHeight]);

  const injectScopedStyles = (headerH, tableHeaderH) => {
    const existing = document.getElementById("a5-invoice-styles");
    if (existing) existing.remove();

    const style = document.createElement("style");
    style.id = "a5-invoice-styles";
    style.innerHTML = `
      @page {
        size: A5 landscape;
        margin: 0.5cm;
      }

      @media print {
        html, body {
          margin: 0;
          padding: 0;
          width: 210mm;
          height: 148.5mm; 
        }
        
        .a5-page {
          page-break-after: always;
          break-after: page;
          width: 100%;
          height: auto;
        }
        
        .a5-page:last-child {
          page-break-after: auto;
          break-after: auto;
        }
        
        .a5-header-section {
          position: relative;
          background: white;
          margin-top: 0;
          padding-top: 0;
          page-break-inside: avoid;
          break-inside: avoid;
        }

        .a5-table-header-section {
          position: relative;
          background: white;
          page-break-inside: avoid;
          break-inside: avoid;
        }
        
        .a5-landscape {
          width: 100%;
          max-width: 210mm;
          margin: 0 auto;
        }
      }

      .a5-invoice-table {
        table-layout: fixed;
        width: 100%;
      }

      col-sno    { width: 5%; }
      col-desc   { width: 35%; }
      col-unit   { width: 8%; }
      col-qty    { width: 8%; }
      col-ws     { width: 12%; }
      col-disc   { width: 10%; }
      col-foc    { width: 6%; }
      col-vat    { width: 9%; }
      col-total  { width: 7%; }
      
      @media print {
        body {
          font-size: 9pt;
        }
        
        .a5-invoice-table th,
        .a5-invoice-table td {
          font-size: 7pt;
        }
      }
    `;
    document.head.appendChild(style);
  };

  const renderHeader = () => (
    <div className="a5-header-section" ref={headerRef}>
      <div className="flex justify-between items-start">
        <div className="text-black" style={{ fontSize: "0.7rem" }}>
          <div className="font-semibold text-sm">
            {capitalizeWords(values?.branch?.name)}
          </div>
          <div>Email : {values?.branch?.email}</div>
          <div>Phone : {values?.branch?.phone}</div>
          <div>
            {values?.branch?.area ? values?.branch?.area + ", " : ""}
            {values?.branch?.city ? values?.branch?.city + ", " : ""}
            {formatCountryName(values?.branch?.country)}
          </div>
          <div>VATIN : {values?.branch?.tax || "N/A"}</div>
        </div>

        {values?.branch?.image ? (
          <div className="flex flex-col items-center">
            <img
              className="w-[40px] h-[40px]"
              src={`${URL}/uploads${values?.branch?.image}`}
              alt="Branch Logo"
            />
            <div className="font-semibold text-sm">
              {capitalizeWords(values?.branch?.name)}
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center">
            <div className="w-[40px] h-[40px] rounded-md bg-gray-300 text-black flex items-center justify-center text-lg font-semibold">
              {values?.branch?.name
                ? values.branch.name.charAt(0).toUpperCase()
                : "N"}
            </div>
            <div className="font-semibold text-sm">
              {capitalizeWords(values?.branch?.name)}
            </div>
          </div>
        )}
      </div>

      <hr className="my-1 border-black" />
      <div className="text-center text-xs py-0.5 font-semibold">
        TAX INVOICE
      </div>
      <hr className="my-1 border-black" />

      <div className="border border-gray-300 rounded-lg p-1 mb-1 shadow-sm">
        <div className="flex justify-between">
          <div className="space-y-0.5" style={{ fontSize: "0.7rem" }}>
            <div className="font-semibold mb-1">Customer</div>
            <div className="font-medium">
              {values?.customer?.name || "Walk-in Customer"}
            </div>
            {values?.customer?.phone && (
              <div>
                <span className="font-semibold">Phone :</span>{" "}
                {values?.customer?.phone}
              </div>
            )}
            {values?.customer?.email && (
              <div>
                <span className="font-semibold">Email :</span>{" "}
                {values?.customer?.email}
              </div>
            )}
            <div>
              {values?.customer?.city ? values?.customer?.city + ", " : ""}
              {values?.customer?.state}
            </div>
            <div>{formatCountryName(values?.customer?.country)}</div>
            {values?.customer?.tax && (
              <div>
                <span className="font-semibold">VATIN :</span>{" "}
                {values?.customer?.tax}
              </div>
            )}
          </div>

          <div className="space-y-0.5" style={{ fontSize: "0.7rem" }}>
            <div className="flex">
              <div className="w-20 font-bold">Invoice No</div>
              <div className="px-1">:</div>
              <div>#INV-{values?.number}</div>
            </div>
            <div className="flex">
              <div className="w-20 font-bold">Invoice Type</div>
              <div className="px-1">:</div>
              <div>
                {values?.payment_status == 2
                  ? values?.payments
                      ?.map((v, i) => (i > 0 ? ", " : "") + v?.name)
                      .join("")
                  : "CREDIT"}
              </div>
            </div>
            <div className="flex">
              <div className="w-20 font-bold">Ref</div>
              <div className="px-1">:</div>
              <div>
                {values?.converted_from_booking
                  ? `Booking #${values?.converted_from_booking?.number}`
                  : values?.delivery_note
                    ? `DN #${values?.delivery_note?.number}`
                    : "Direct"}
              </div>
            </div>
            <div className="flex">
              <div className="w-20 font-bold">Date</div>
              <div className="px-1">:</div>
              <div>{moment(values?.date).format("DD-MM-YYYY")}</div>
            </div>
            {values?.due_date && (
              <div className="flex">
                <div className="w-20 font-bold">Due Date</div>
                <div className="px-1">:</div>
                <div>{moment(values?.due_date).format("DD-MM-YYYY")}</div>
              </div>
            )}
            {values?.lpo_number && (
              <div className="flex">
                <div className="w-20 font-bold">LPO No</div>
                <div className="px-1">:</div>
                <div>{values?.lpo_number}</div>
              </div>
            )}
            {values?.lpo_date && (
              <div className="flex">
                <div className="w-20 font-bold">LPO Date</div>
                <div className="px-1">:</div>
                <div>{moment(values?.lpo_date).format("DD-MM-YYYY")}</div>
              </div>
            )}
            {values?.salesman?.name && (
              <div className="flex">
                <div className="w-20 font-bold">Salesman</div>
                <div className="px-1">:</div>
                <div>{values?.salesman?.name}</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderTableHeader = () => (
    <div className="a5-table-header-section" ref={tableHeaderRef}>
      <table className="a5-invoice-table w-full border-separate border-spacing-0 rounded-t-lg overflow-hidden border border-gray-300">
        <thead>
          <tr className="bg-gray-100 border-b border-gray-300">
            <th
              className="col-sno text-left py-1 pl-2 border-b border-gray-300 rounded-tl-lg"
              style={{ fontSize: "0.65rem" }}
            >
              S.NO
            </th>
            <th
              className="col-desc text-left p-1 border-b border-gray-300"
              style={{ fontSize: "0.65rem" }}
            >
              DESCRIPTION
            </th>
            <th
              className="col-unit text-left p-1 border-b border-gray-300"
              style={{ fontSize: "0.65rem" }}
            >
              UNIT
            </th>
            <th
              className="col-qty text-left p-1 border-b border-gray-300"
              style={{ fontSize: "0.65rem" }}
            >
              QTY
            </th>
            <th
              className="col-ws text-left p-1 border-b border-gray-300"
              style={{ fontSize: "0.65rem" }}
            >
              RATE
            </th>
            <th
              className="col-disc text-left p-1 border-b border-gray-300"
              style={{ fontSize: "0.65rem" }}
            >
              DISC
            </th>
            <th
              className="col-foc text-left p-1 border-b border-gray-300"
              style={{ fontSize: "0.65rem" }}
            >
              FOC
            </th>
            <th
              className="col-vat text-left p-1 border-b border-gray-300"
              style={{ fontSize: "0.65rem" }}
            >
              VAT
            </th>
            <th
              className="col-total text-right p-1 border-b border-gray-300 rounded-tr-lg"
              style={{ fontSize: "0.65rem" }}
            >
              TOTAL
            </th>
          </tr>
        </thead>
      </table>
    </div>
  );

  const renderTableBody = (pageItems, pageNumber, isLastPage) => (
    <>
      <table className="a5-invoice-table w-full border-separate border-spacing-0 overflow-hidden border border-gray-300 border-t-0">
  <tbody>
    {pageItems.map((value, index) => {
      const globalIndex = (pageNumber - 1) * itemsPerPage + index;
      const isLastOverallRow =
        isLastPage && index === pageItems.length - 1;

      return (
        <tr key={`${pageNumber}-${index}`} className="hover:bg-gray-50">
          <td
            className={`col-sno text-left px-1 py-0 pl-1 ${
              isLastOverallRow ? "rounded-bl-lg" : ""
            }`}
            style={{ fontSize: "0.6rem" }}
          >
            {globalIndex + 1}
          </td>

          <td
            className="col-desc px-1 py-0"
            style={{ fontSize: "0.6rem" }}
          >
            {value?.name}
          </td>

          <td
            className="col-unit text-left px-1 py-0"
            style={{ fontSize: "0.6rem" }}
          >
            {value?.unit_name}
          </td>

          <td
            className="col-qty text-left px-1 py-0"
            style={{ fontSize: "0.6rem" }}
          >
            {value?.quantity}
          </td>

          <td
            className="col-ws text-left px-1 py-0"
            style={{ fontSize: "0.6rem" }}
          >
            {value?.sale_price?.toFixed(3)}
          </td>

          <td
            className="col-disc text-left px-1 py-0"
            style={{ fontSize: "0.6rem" }}
          >
            <div>
              {value?.discount_percentage}% &nbsp;
              {value?.discount_price?.toFixed?.(3)}
            </div>
          </td>

          <td
            className="col-foc text-left px-1 py-0"
            style={{ fontSize: "0.6rem" }}
          >
            {value?.free ? value?.free : 0}
          </td>

          <td
            className="col-vat text-left px-1 py-0 whitespace-nowrap"
            style={{ fontSize: "0.6rem" }}
          >
            <div>
              {value?.tax}% &nbsp;
              {value?.tax_amount?.toFixed(3)}
            </div>
          </td>

          <td
            className="col-total text-right px-1 py-0 pr-1"
            style={{ fontSize: "0.6rem" }}
          >
            {value?.total?.toFixed(3)}
          </td>
        </tr>
      );
    })}

    {pageItems.length < itemsPerPage &&
      Array.from({ length: itemsPerPage - pageItems.length }).map(
        (_, index, arr) => {
          const lastRowRounded =
            isLastPage && index === arr.length - 1;

          return (
            <tr
              key={`empty-${pageNumber}-${index}`}
              className="hover:bg-gray-50"
            >
              <td
                className={`px-1 py-0 pl-1 ${
                  lastRowRounded ? "rounded-bl-lg" : ""
                }`}
                style={{ fontSize: "0.6rem" }}
              >
                &nbsp;
              </td>

              <td className="px-1 py-0">&nbsp;</td>
              <td className="px-1 py-0">&nbsp;</td>
              <td className="px-1 py-0">&nbsp;</td>
              <td className="px-1 py-0">&nbsp;</td>
              <td className="px-1 py-0">&nbsp;</td>
              <td className="px-1 py-0">&nbsp;</td>
              <td className="px-1 py-0">&nbsp;</td>

              <td
                className={`text-right px-1 py-0 pr-1 ${
                  lastRowRounded ? "rounded-br-lg" : ""
                }`}
                style={{ fontSize: "0.6rem" }}
              >
                &nbsp;
              </td>
            </tr>
          );
        }
      )}
  </tbody>
</table>

      {isLastPage && (
        <div className="border border-gray-300 rounded-md p-2 mb-1 mt-1 shadow-sm">
          <div className="flex justify-between">
            <div className="w-1/2 pr-2" style={{ fontSize: "0.65rem" }}>
              <div className="font-semibold mb-0.5">
                Total in words:&nbsp;
                <span className="font-normal">
                  <CurrencyWord amount={values?.total?.toFixed(3)} />
                </span>
              </div>

              {(values?.branch?.bank_name || values?.branch?.bank_swift) && (
                <div className="mt-1 space-y-0">
                  {values?.branch?.bank_name && (
                    <div className="flex gap-1 leading-tight">
                      <span className="font-semibold w-16">Bank Name</span>
                      <span>: {values?.branch?.bank_name}</span>
                    </div>
                  )}
                  {values?.branch?.bank_account_name && (
                    <div className="flex gap-1 leading-tight">
                      <span className="font-semibold w-16">A/C Name</span>
                      <span>: {values?.branch?.bank_account_name}</span>
                    </div>
                  )}
                  {values?.branch?.bank_branch && (
                    <div className="flex gap-1 leading-tight">
                      <span className="font-semibold w-16">Branch</span>
                      <span>: {values?.branch?.bank_branch}</span>
                    </div>
                  )}
                  {values?.branch?.bank_account_number && (
                    <div className="flex gap-1 leading-tight">
                      <span className="font-semibold w-16">A/C Number</span>
                      <span>: {values?.branch?.bank_account_number}</span>
                    </div>
                  )}
                  {values?.branch?.bank_swift && (
                    <div className="flex gap-1 leading-tight">
                      <span className="font-semibold w-16">Swift Code</span>
                      <span>: {values?.branch?.bank_swift}</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="w-1/2" style={{ fontSize: "0.65rem" }}>
              <table className="w-full">
                <tbody>
                  <tr>
                    <td className="text-right pr-1 py-0">Subtotal</td>
                    <td className="text-right w-16 py-0">
                      {values?.subtotal?.toFixed(3)}
                    </td>
                  </tr>
                  <tr>
                    <td className="text-right pr-1 py-0">VAT (5%)</td>
                    <td className="text-right py-0">
                      {values?.taxamount?.toFixed(3)}
                    </td>
                  </tr>
                  <tr>
                    <td className="text-right pr-1 py-0">Discount</td>
                    <td className="text-right py-0">
                      {values?.discount_type
                        ? values?.discount_value
                          ? `${values?.discount?.toFixed(3)} (-${values?.discount_value}%)`
                          : "0.000"
                        : values?.discount
                          ? `-${values?.discount?.toFixed(3)}`
                          : "0.000"}
                    </td>
                  </tr>
                  <tr>
                    <td className="text-right pr-1 py-0">Delivery Charges</td>
                    <td className="text-right py-0">
                      {values?.delivery?.toFixed(3) || "0.000"}
                    </td>
                  </tr>
                  <tr>
                    <td className="text-right pr-1 pt-0.5 font-semibold">
                      Total (<UserCurrency />)
                    </td>
                    <td className="text-right pt-0.5 font-semibold">
                      {values?.total?.toFixed(3)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {isLastPage && values?.note && (
        <div
          className="pt-2"
          style={{ fontSize: "0.65rem" }}
          dangerouslySetInnerHTML={{ __html: values?.note }}
        />
      )}
    </>
  );

  return (
    <div className="a5-landscape" ref={ref}>
      <style>{`
        @media print {
          @page {
            size: A5 landscape;
            margin: 0.5cm;
          }
          
          * {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          
          .a5-landscape {
            width: 100%;
            margin: 0;
            padding: 0;
          }
          
          .a5-page {
            page-break-after: always;
            break-after: page;
            margin: 0;
            padding: 0;
            width: 100%;
            box-sizing: border-box;
          }
          
          .a5-page:last-child {
            page-break-after: auto;
            break-after: auto;
          }
          
          body {
            margin: 0;
            padding: 0;
            width: 210mm;
          }
        }
        
        .a5-page {
          margin-bottom: 20px;
          border: 1px dashed #ccc;
          padding: 10px;
        }
        
        @media print {
          .a5-page {
            border: none;
            padding: 0;
            margin: 0;
          }
        }
      `}</style>

      {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNumber) => {
        const pageItems = getItemsForPage(pageNumber);
        const isLastPage = pageNumber === totalPages;

        return (
          <div
            key={pageNumber}
            className="a5-page"
            style={{ pageBreakBefore: pageNumber === 1 ? "auto" : "always" }}
          >
            {renderHeader()}
            {renderTableHeader()}
            {renderTableBody(pageItems, pageNumber, isLastPage)}
          </div>
        );
      })}
    </div>
  );
});

export default InvoiceA5;
